<template>
  <CollapsableSection title="Personal Employee Information">
    <div :class="[$style.inputRow, $style.twoThirds]">
      <div :class="$style.flexSingleWidth">
        <InputValidator
          v-model="personalHome.number"
          :config="{
            attribute: 'number',
            disabled: isRestrictedField('phoneNumbers'),
            label: 'Home Phone',
            name: 'Home Phone',
            value: personalHome.number,
          }"
          @InputValidatorModel="handleValueChange(
            setFirstOf(...arguments)(item.phoneNumbers, 'home'),
            'phoneNumbers'
          )"
        />
      </div>
      <div :class="$style.flexSingleWidth">
        <InputValidator
          v-model="personalMobile.number"
          :config="{
            attribute: 'number',
            disabled: isRestrictedField('phoneNumbers'),
            label: 'Personal Cell',
            name: 'Personal Cell',
            value: personalMobile.number,
          }"
          @InputValidatorModel="handleValueChange(
            setFirstOf(...arguments)(item.phoneNumbers, 'mobile'),
            'phoneNumbers'
          )"
        />
      </div>
    </div>
    <div :class="$style.inputRow">
      <div :class="$style.flexDoubleWidth">
        <InputValidator
          v-model="personalAddress.streetAddress"
          :config="{
            attribute: 'streetAddress',
            disabled: isRestrictedField('addresses'),
            label: 'Home Street Address',
            name: 'Home Street Address',
            value: personalAddress.streetAddress,
          }"
          @InputValidatorModel="handleValueChange(
            setFirstOf(...arguments)(item.addresses, 'home'),
            'addresses'
          )"
        />
      </div>
      <div :class="$style.flexSingleWidth">
        <InputValidator
          v-model="personalAddress.poBox"
          :config="{
            attribute: 'poBox',
            disabled: isRestrictedField('addresses'),
            label: 'Home PO Box',
            name: 'Home PO Box',
            validate: 'max:50|numeric',
            value: personalAddress.poBox,
          }"
          @InputValidatorModel="handleValueChange(
            setFirstOf(...arguments)(item.addresses, 'home'),
            'addresses'
          )"
          @fInputValidatorBlur="handleBlur(
            personalAddress.poBox,
            'PO Box',
            'PO Box')"
        />
      </div>
    </div>
    <div :class="$style.inputRow">
      <div :class="$style.flexSingleWidth">
        <InputValidator
          :config="{
            attribute: 'locality',
            disabled: isRestrictedField('addresses'),
            label: 'Home City',
            name: 'Home City',
            value: personalAddress.locality,
          }"
          @InputValidatorModel="handleValueChange(
            setFirstOf(...arguments)(item.addresses, 'home'),
            'addresses'
          )"
        />
      </div>
      <div :class="$style.flexSingleWidth">
        <InputValidator
          :config="{
            attribute: 'region',
            disabled: isRestrictedField('addresses'),
            label: 'Home State',
            name: 'Home State',
            value: personalAddress.region,
          }"
          @InputValidatorModel="handleValueChange(
            setFirstOf(...arguments)(item.addresses, 'home'),
            'addresses'
          )"
        />
      </div>
      <div :class="$style.flexSingleWidth">
        <InputValidator
          :config="{
            attribute: 'postalCode',
            disabled: isRestrictedField('addresses'),
            label: 'Home Postal Code',
            name: 'Home Postal Code',
            value: personalAddress.postalCode,
          }"
          @InputValidatorModel="handleValueChange(
            setFirstOf(...arguments)(item.addresses, 'home'),
            'addresses'
          )"
        />
      </div>
    </div>
    <div :class="[$style.inputRow, $style.half]">
      <DropDown
        attribute="country"
        :clearable="true"
        :disabled="isRestrictedField('addresses')"
        label="Home Country"
        name="Home Country"
        optionLabelKey="Name"
        :options="countries"
        optionValueKey="Code"
        :value="personalAddress.country"
        @input="handleValueChange(
          setFirstOf(...arguments)(item.addresses, 'home'),
          'addresses'
        )"
      />
    </div>
    <InformationalText
      v-if="!systemUsersCanEdit"
      :text="getUserFieldsReadOnlyText()"
    />
  </CollapsableSection>
</template>

<script>
import { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import { mapGetters } from 'vuex';
import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import CollapsableSection from 'client/components/ContentSection/CollapsableSection.vue';
import DropDown from 'client/components/FormValidation/DropDown.vue';
import InformationalText from 'client/components/ContentSection/InformationalText.vue';
import InputValidator from 'client/components/FormValidation/InputValidator.vue';
import UserDetailsMethodsMixin from 'client/systemusers/UserDetails/mixins/UserDetailsMethodsMixin';
import countries from 'client/data/countries.json';

const storeModel = StoreModelMap[ItemTypeConsts.systemUser];

export default {
  name: 'PersonalEmployeeInformation',

  components: {
    CollapsableSection,
    DropDown,
    InformationalText,
    InputValidator,
  },

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
    UserDetailsMethodsMixin,
  ],

  props: {
    handleValueChange: {
      type: Function,
      required: true,
    },
  },

  data: () => ({
    countries,
  }),

  computed: {
    ...mapGetters(storeModel, ['isRestrictedField']),
    ...mapGetters('SettingsModule', ['systemUsersCanEdit']),

    personalAddress() {
      return this.getFirstOf('home', this.item.addresses);
    },

    personalHome() {
      return this.getFirstOf('home', this.item.phoneNumbers);
    },

    personalMobile() {
      return this.getFirstOf('mobile', this.item.phoneNumbers);
    },

  },

  inject: ['$validator'],

  beforeCreate() {
    this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options);
  },

  mounted() {
    this.$validator.localize('en', this.$options.vValidateDict);
  },

  methods: {
    handleBlur(value, key, name) {
      this.handleValueChange(String(value).trim(), key);
      this.$nextTick()
        .then(() => this.$validator.validate(name));
    },
  },

  vValidateDict: {
    custom: {
      'Home PO Box': {
        numeric: '* Enter a valid PO Box.',
        max: '* Maximum 50 characters reached.',
      },
    },
  },

};
</script>

<style module>
@import '../../css/flex.css';
</style>
